import { GameServices } from "game/GameServices";
import { View } from "@westpierstudio/luma/mvc";
import { FullScreenPanel } from "./FullScreenPanel";
import { VSHistory } from "./VSHistory";
import { VSScreenButton } from "./VSScreenButton";
import { Signal } from "type-signals";
import { Tween } from "@westpierstudio/luma/createjs";
import { VSHistoryData } from "game/DragonsVsPandasGameModel";
import { AdvancedText } from "@westpierstudio/luma/utils/advancedtext";
import { Text } from "pixi.js";
import { Container, Sprite } from "pixi.js";
import { minimumHeightSpecSelector, orientationSpecSelector } from "utils/Responsive";
import { BoundedContainer, ImplementBoundedContainer } from "utils/BoundedContainer";

enum FLASH_STAGE {
    ONE,
    TWO
}

class VSScreenSignals {
    dragonClick = new Signal();
    pandaClick = new Signal();
    animateOutComplete = new Signal();
}

export class VSScreen extends View {
    private readonly scalingContainer: Container;
    private readonly menuContainer: Container;
    private readonly panel: FullScreenPanel;
    private readonly landscapeHistoryPanel: Sprite;
    private readonly portraitHistoryPanel: Sprite;
    private readonly history: VSHistory;
    private readonly chooseText: Text | AdvancedText;
    private readonly chooseContainer: BoundedContainer;
    private readonly orText: Text | AdvancedText;
    private readonly orContainer: BoundedContainer;
    private readonly dragonButton: VSScreenButton;
    private readonly pandaButton: VSScreenButton;
    private readonly logo: Sprite;
    private readonly portraitLogo: Sprite;
    private readonly spec: any;
    signals = new VSScreenSignals();

    constructor(protected override readonly services: GameServices) {
        super(services);

        const {factory, resources} = this.services;

        this.spec = resources.getSpec(`vsscreen`);
        factory.applySpec(this, {x: 160, y: 100});

        this.scalingContainer = factory.createContainer({pivot: {x: 160, y: 100}}, this);
        this.scalingContainer.addChild(this.panel = new FullScreenPanel(this.services));

        this.menuContainer = factory.createContainer({anchor: 0.5}, this.panel.contentContainer);

        this.logo = factory.createSprite(this.spec.VSLogo, this.panel.contentContainer);
        this.portraitLogo = factory.createSprite(resources.getSpec(`logo`).vs_logo, this.panel.contentContainer);

        this.landscapeHistoryPanel = factory.createSprite(this.spec.gamesPlayedPanel, this);
        this.panel.contentContainer.addChild(this.landscapeHistoryPanel);

        this.portraitHistoryPanel = factory.createSprite(this.spec.gamesPlayedPanelPortrait, this);
        this.panel.contentContainer.addChild(this.portraitHistoryPanel);

        this.panel.contentContainer.addChild(this.history = new VSHistory(this.services, this.spec));

        this.chooseText = factory.createText(resources.getMessage(`vsChoose`), this.spec.vsChoose, this.menuContainer);
        this.chooseContainer = ImplementBoundedContainer(this.chooseText, this.spec.vsChoose.maxBounds);
        this.chooseContainer.update();

        this.orText = factory.createText(resources.getMessage(`vsOR`), this.spec.vsOR, this.menuContainer);
        this.orContainer = ImplementBoundedContainer(this.orText, this.spec.vsOR.maxBounds);
        this.orContainer.update();

        this.menuContainer.addChild(this.dragonButton = new VSScreenButton(this.services, this.spec.dragonButton, `Dragon Reels`, 0xFF0000));
        this.dragonButton.signals.onClick.add(() => this.onButtonClick(this.signals.dragonClick));

        this.menuContainer.addChild(this.pandaButton = new VSScreenButton(this.services, this.spec.pandaButton, `Panda Reels`, 0x00FF00));
        this.pandaButton.signals.onClick.add(() => this.onButtonClick(this.signals.pandaClick));

        this.visible = false;
    }

    show(vsHistoryData?: VSHistoryData): void {

        this.history.set(vsHistoryData);

        this.panel.enableResize();
        this.visible = true;
    }

    enable(): void {
        this.services.playerInactivity.start();
        this.dragonButton.setInteractive();
        this.pandaButton.setInteractive();
    }

    animateOut(): void {

        this.panel.disableResize();

        const {scaleDownTime} = this.services.resources.getSetting(`vsScreen`);

        Tween.get(this.scalingContainer.scale)
        .to({x: 0, y: 0}, scaleDownTime)
        .call(() => {

            this.visible = false;
            this.scalingContainer.scale.set(1.0, 1.0);
            this.history.reset();
            this.signals.animateOutComplete.dispatch();
        });
    }

    resize(): void {

        if (this.services.layout.getScreenBoundaryInBucketSpace().height <= 320) {
            this.logo.visible = true;
            this.landscapeHistoryPanel.visible = true;
            this.portraitHistoryPanel.visible = false;
            this.portraitLogo.visible = false;

            this.services.factory.applySpec(this.menuContainer, this.spec.landscape.menu);

            this.history.setLandscape();
        } else {
            this.logo.visible = false;
            this.landscapeHistoryPanel.visible = false;
            this.portraitHistoryPanel.visible = true;

            const logoPortraitSpec = minimumHeightSpecSelector(this.services, this.spec.portrait.logo);
            this.services.factory.applySpec(this.portraitLogo, orientationSpecSelector(this.services, this.spec, logoPortraitSpec));

            const menuPortraitSpec = minimumHeightSpecSelector(this.services, this.spec.portrait.menu);
            this.services.factory.applySpec(this.menuContainer, orientationSpecSelector(this.services, this.spec, menuPortraitSpec));

            const historyPanelPortraitSpec = minimumHeightSpecSelector(this.services, this.spec.portrait.historyPanel);
            this.services.factory.applySpec(this.portraitHistoryPanel, orientationSpecSelector(this.services, this.spec, historyPanelPortraitSpec));

            this.history.setPortrait();
        }
    }

    override reset(): void {

        this.visible = false;
        Tween.removeTweens(this.scalingContainer);
        this.scalingContainer.scale.set(1.0, 1.0);
    }

    flashLoop(stage: FLASH_STAGE = FLASH_STAGE.ONE): void {

        if (!this.visible) return;

        const {startFlashes, onTime, offTime, holdTime} = this.services.resources.getSetting(`vsScreen`).flash;

        const tween = Tween.get(this);

        if (stage === FLASH_STAGE.ONE) {
            for (let i = 0; i < startFlashes; i++)
            {
                tween.wait(onTime)
                .call(() => {
                    this.dragonButton.setGlowOff();
                    this.pandaButton.setGlowOff();
                })
                .wait(offTime)
                .call(() => {
                    this.dragonButton.setGlowOn();
                    this.pandaButton.setGlowOn();
                });
            }

            tween.wait(holdTime);
        } else {
            tween.wait(onTime)
                .call(() => {
                    this.dragonButton.setGlowOff();
                    this.pandaButton.setGlowOff();
                    this.chooseText.visible = false;
                })
                .wait(offTime)
                .call(() => {
                    this.dragonButton.setGlowOn();
                    this.pandaButton.setGlowOn();
                    this.chooseText.visible = true;
                });
        }

        tween.call(() => {
            this.flashLoop(FLASH_STAGE.TWO);
        });
    }

    private onButtonClick(signal: Signal): void {

        this.services.playerInactivity.stop();

        Tween.removeTweens(this);

        this.dragonButton.setGlowOn();
        this.pandaButton.setGlowOn();
        this.chooseText.visible = true;

        Tween.get(this)
        .wait(this.services.resources.getSetting(`vsScreen`).buttonClickHoldTime)
        .call(() => {

            this.dragonButton.reset();
            this.pandaButton.reset();

            signal.dispatch();
        });
    }
}